import React, {useEffect} from "react";
import styles from "./BotTable.module.css";
import {BotTableHeader} from "./header/BotTableHeader";
import {BotTableRow} from "./row/BotTableRow";
import {BotTableControls} from "./controls/BotTableControls";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {
    BOT_TABLE_ITEMS_ON_PAGE,
    botListRequestStatusSelector,
    botListSelector,
    botListTotalCountSelector,
    botTableRequestSelector,
    fetchBotList,
    updateBotTableRequest
} from "../botListSlice";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {TableLoader} from "../../../elements/tableLoader/TableLoader";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";

interface BotTableProps {
}

export const BotTable: React.FC<BotTableProps> = () => {

    const dispatch = useAppDispatch()
    const botList = useAppSelector(botListSelector)
    const botListTotalCount = useAppSelector(botListTotalCountSelector)
    const botListRequestStatus = useAppSelector(botListRequestStatusSelector)
    const botTableRequest = useAppSelector(botTableRequestSelector)

    useEffect(() => {
        dispatch(fetchBotList())
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botTableRequest]);

    useEffect(() => {
        if (botList.length === 0 && botTableRequest.pageNumber > 0 && botListRequestStatus === RequestStatus.RESOLVED) {
            dispatch(updateBotTableRequest({pageNumber: botTableRequest.pageNumber - 1}))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [botList.length, botTableRequest.pageNumber, botListRequestStatus]);

    const handleCreateAssistantClick = () => {
        dispatch(openForm({name: FormName.CREATE_BOT}))
    }

    const hasActiveFilters = () => {
        return botTableRequest.searchQuery ||
               (botTableRequest.botTypes && botTableRequest.botTypes.length > 0) ||
               (botTableRequest.franchisePublicIds && botTableRequest.franchisePublicIds.length > 0);
    }

    if (botListRequestStatus === RequestStatus.REJECTED) {
        dispatch(showErrorToastNotification({message: "Failed to load assistants"}))
    }

    const isLoading = botListRequestStatus === RequestStatus.PENDING;
    const isResolved = botListRequestStatus === RequestStatus.RESOLVED;

    return <div className={styles.wrapper}>
        <div className={styles.tableWrapper}>
            <BotTableControls/>
            <BotTableHeader/>
            <div className={styles.tableRowsWrapper}>
                {isLoading && <TableLoader />}

                {isResolved &&
                    botList.map((bot, index) => {
                    return <React.Fragment key={`assistant-table-row-${index}`}>
                        <BotTableRow
                            index={index}
                            bot={bot}
                            isLastRow={index === (botList.length - 1)}
                        />
                    </React.Fragment>
                })}
            </div>
        </div>

        {isResolved && botListTotalCount === 0 && !hasActiveFilters() && (
            <div className={styles.emptyTableWrapper}>
                <div className={styles.titleWrapper}>
                    <span className={styles.title}>You haven’t created any assistants yet</span>
                    <span className={styles.subtitle}>
            Get started by creating an assistant. Once created, it will appear here.
          </span>
                </div>
                <Button
                    title={"Create assistant"}
                    iconSrc={"/plusIcon.svg"}
                    state={ButtonState.PRIMARY}
                    handleClick={handleCreateAssistantClick}
                />
            </div>
        )}

        {isResolved && !(botListTotalCount === 0 && !hasActiveFilters()) && (
            <TablePagination
                pageNumber={botTableRequest.pageNumber}
                totalItems={botListTotalCount}
                itemsPerPage={BOT_TABLE_ITEMS_ON_PAGE}
                onPageChange={(newPage) => dispatch(updateBotTableRequest({pageNumber: newPage}))}
            />
        )}
    </div>
}
